import { useEffect, useState, type ReactNode } from 'react';
import { Alert, Button, Card, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { PAAccount, PAContract, PADashboard, PASuspension } from '../api/money';
import { getDashboard } from '../api/money';
import {
  activateAccount,
  getActivationOptions,
  type PAActivationOption,
  type PAActivationOptions,
} from '../api/access';
import { capabilityOr, useCapabilities } from '../access/useCapabilities';
import { NO_VALUE, formatInstant, formatInstantDay } from '../format';
import { Money } from '../money/Money';
import { FactList } from '../money/DataTable';
import { MoneyPage, QueryFrame } from '../money/MoneyPage';
import { usePAQuery } from '../money/usePAQuery';
import {
  accountStatusLabel,
  accountStatusTone,
  billingModelLabel,
  contractStatusLabel,
  cycleStateLabel,
  floorLabel,
  recommendedModeLabel,
  suspensionReasonLabel,
} from '../money/labels';
import { errorText } from '../session/errorText';
import type { PASubscriber } from '../api/pa';
import type { Navigation } from '../router';

/**
 * ============================================================================
 * THE DASHBOARD — «Мои аккаунты» (Appendix L §3, `ui-enduser-personal-area` §3)
 * ============================================================================
 *
 * ONE READ produces every figure on this page (`GET /pa/money/dashboard`), and that is the
 * server's decision, not a convenience: a screen that fetched the balance, then the
 * recommendation, then the resume figure could straddle a payment and print a resume amount
 * computed against a balance it is not showing. Reconciling those two numbers is exactly what
 * a subscriber telephones about.
 *
 * ⚠ THE ONE RULE OF THIS FILE: **no figure on it is computed here.** Every amount is a field
 * the server sent, printed through `<Money>`, which takes one integer and has nowhere to put
 * a second operand. In particular:
 *
 *   - **the suspension notice prints `suspension.amount` VERBATIM.** It is
 *     `billing.RecommendedPaymentModeQuote(contract, RecommendModeResume)` — the same number
 *     the OSMP terminal, the payment window and the suspension SMS quote for this Contract at
 *     this instant. `requirement` and `money` are shown BESIDE it as context, and the screen
 *     never derives one from the others. `amount` is NOT `requirement − availability`: an
 *     incoming payment repays an active promise FIRST, so the first rubles move availability
 *     by nothing (D64/D74), and the requirement is the PACK rather than the fee (D67);
 *   - **there is no per-Account resume figure**, because the resume gate reads the CONTRACT's
 *     availability and whichever Account the reactor reaches first spends the payment. Two
 *     Accounts at 500 each once produced two notices reading «внесите 500,00» beside a
 *     terminal reading 1000,00, and paying 500 restored half the service. The wire carries no
 *     such field and this screen adds none — the notice lists WHAT is cut off and quotes ONE
 *     figure for the lot;
 *   - **`cycle_cost` is labelled «Стоимость периода» and NOT «Следующий платёж»**
 *     *(corrected 2026-08-12, W2 review A2 + the mutation lens's §3.3)*. The SERVER is right
 *     and was verified so: `billing.recommendCycleCost` prices §3.3 mode 0 at status ACTIVE
 *     deliberately — so the figure does not collapse to zero for the weeks a subscriber
 *     spends in a vacation block on a tariff with no block price — and AMORTIZES own-anchor
 *     periodic services over the period instead of counting the fires inside it. It is a
 *     cost of ownership. Calling it «Следующий платёж» made the screen promise a debit: a
 *     dacha account on a winter block was quoted the full tariff for a period §3.15 charges
 *     the block price for, or nothing at all. The figure is untouched; the label is what
 *     changed, and the DATE now sits on its own row saying only what it is.
 *     UNDER MODEL A THE FIGURE IS THE MONTH, NOT THE DAY — no exported engine definition of
 *     a Model-A daily figure exists, so the row labels the model rather than inventing one.
 *   - **«Оплачено до» is only said about a period that WAS paid.** `coverage_until` is the
 *     end of the current cycle's window whatever its state, and asserting a payment over it
 *     was reproduced live: a subscriber who paid 0,00 and was suspended at activation read
 *     «Оплачено до 05.09.2026» directly under «Внесите 450,00». The date stays; the claim
 *     follows `cycle_state`.
 *   - **a suspended contract with NO figure says so.** `suspended_without_figure` is the
 *     server's answer to «why is there no notice on a screen that is showing red», and
 *     rendering nothing for it is what made an unpriceable tariff and an imported status-1
 *     account — every one of them, at cutover — indistinguishable from a working page.
 *
 * # ⚠ THE NOTICE NOW CARRIES AN ACTION, AND THAT IS THE WHOLE OF W3
 *
 * ~~**«Пополнить»** — the card top-up is W3. A button that opens nothing is the D109 dead
 * control this phase has already removed twice.~~ *(struck 2026-08-12: the screen behind it
 * exists.)* The notice quotes the engine's figure and then offers the two ways to act on
 * it — «Оплатить», which opens «Оплата» with that same figure PRE-FILLED BY THE SERVER, and
 * «Другие способы оплаты», §8e's reading page, so a subscriber who cannot pay by card is one
 * press from the office and terminal instructions rather than one telephone call.
 *
 * ⚠ THE BUTTON CARRIES NO FIGURE WITH IT. It navigates, and the payment form reads
 * `prefill_amount` from `GET /pa/payments/options` — the SERVER's own selection rule
 * («for a suspended Account the recommended and pre-filled amount is the EXACT amount
 * required to resume»). Passing the notice's number through the router would be a second
 * copy of the one figure this project has had wrong three times, and it would be a STALE
 * copy the moment a payment lands.
 *
 * # ⚠ AND FROM W4 IT CARRIES «АКТИВИРОВАТЬ» (F18 / §12 / D45)
 *
 * §12 puts the button in the account table's STATUS column and this screen does the same —
 * in the card's status corner, beside «Ожидает активации», which is the label that stops
 * status 10 reading as «отключено». Three properties of it are load-bearing:
 *
 *   - **it is offered only when the OPERATOR has enabled self-activation.**
 *     `self_activation_enabled` comes from `GET /pa/accounts/capabilities`, and a pending or
 *     FAILED capability read is treated as «off» (`capabilityOr(..., false)`): the write
 *     route refuses with `409 pa.activation_disabled`, so a button offered without the
 *     capability is a control whose only possible outcome is an error a subscriber cannot
 *     act on. The server itself fails in the same direction when it cannot read the setting;
 *   - **the dialog quotes NO figure.** «Нужно для активации» and «Не хватает» are already on
 *     this card, from the gate's own definition; a third number written into a dialog is how
 *     two surfaces come to disagree about one gate. The charge is the engine's and this
 *     screen holds none of it;
 *   - **a refusal for money offers the way out.** `409 accounts.insufficient_balance` is
 *     rendered as the server's own Russian sentence with «Пополнить счёт» beside it — the
 *     shortfall is on the card above and the payment form reads its own prefill from the
 *     server, so nothing is carried through the router.
 *
 * # What is deliberately NOT on this page
 *
 *   - **the per-account ▾ menu** (§3's «Расход средств / История тарифных планов / Смена
 *     пароля»): «Расход средств» and the Account password are sections of their own in this
 *     build, and the tariff-plan history is not built.
 *   - **the news banner** (§3's pop-up): the message domain has no subscriber-facing read.
 *   - **the Operator's NAME.** The wire carries `operator_id` and no name — the organization
 *     registry is another domain's and a portal showing the WRONG operator is worse than one
 *     showing none.
 */
export function DashboardScreen({
  subscriber,
  expiresAt,
  navigate,
}: {
  subscriber: PASubscriber;
  expiresAt: string;
  navigate: Navigation['navigate'];
}) {
  const { t } = useTranslation();
  // display_name may be empty (a card with no name filled in); the login is the fallback the
  // OpenAPI schema itself names, and without it the greeting reads «Здравствуйте, ».
  const name = subscriber.display_name || subscriber.login;

  return (
    <>
      {/* The two identity prompts, and the identity read-out below, sit OUTSIDE `MoneyPage`.
          That is deliberate: `MoneyPage` renders its children only once a contract has been
          chosen, and a subscriber whose card carries NO live contract — a terminated one whose
          contracts are archived — would otherwise be shown an empty page with no login, no
          «Изменить пароль» and no temporary-password prompt. The money blocks depend on a
          contract; his identity does not. */}
      <IdentityPrompts subscriber={subscriber} navigate={navigate} />

      <MoneyPage
        titleKey="dashboard.title"
        lead={<p className="pa-page-lead">{t('home.greeting', { name })}</p>}
      >
        {(contract) => <DashboardBody contract={contract} navigate={navigate} />}
      </MoneyPage>

      <IdentityCard subscriber={subscriber} expiresAt={expiresAt} navigate={navigate} />
    </>
  );
}

/**
 * The two prompts the SESSION carries rather than the money — each answering a question the
 * subscriber would otherwise put to support.
 *
 *   must_change_password — «my password came from the operator». It PROMPTS and does not
 *     block: the server reports the flag and enforces nothing, so this is an alert with the
 *     one action that clears it rather than a modal nobody can escape.
 *   ip_restricted — «why does my login work at home and not at work». The refusal itself is
 *     byte-identical to a wrong password, on purpose, so this is the ONLY place it can be
 *     explained — to somebody who has already authenticated.
 */
function IdentityPrompts({
  subscriber,
  navigate,
}: {
  subscriber: PASubscriber;
  navigate: Navigation['navigate'];
}) {
  const { t } = useTranslation();
  return (
    <>
      {subscriber.must_change_password ? (
        <Alert
          type="warning"
          showIcon
          message={t('home.temporaryTitle')}
          description={t('home.temporaryText')}
          action={
            <Button type="primary" onClick={() => navigate({ kind: 'password' })}>
              {t('home.changePassword')}
            </Button>
          }
          style={{ marginBottom: 20 }}
        />
      ) : null}
      {subscriber.ip_restricted ? (
        <Alert
          type="info"
          showIcon
          message={t('home.ipRestrictedTitle')}
          description={t('home.ipRestrictedText')}
          style={{ marginBottom: 20 }}
        />
      ) : null}
    </>
  );
}

/**
 * The identity read-out W1b shipped, kept on the landing page because both of its facts are
 * ones a subscriber reads back to support over the telephone — and because the one action this
 * build offers him lives beside them.
 */
function IdentityCard({
  subscriber,
  expiresAt,
  navigate,
}: {
  subscriber: PASubscriber;
  expiresAt: string;
  navigate: Navigation['navigate'];
}) {
  const { t } = useTranslation();
  return (
    <>
      <Card className="pa-block">
        <FactList
          facts={[
            {
              key: 'login',
              label: t('home.loginFact'),
              value: <span className="mono">{subscriber.login}</span>,
            },
            { key: 'session', label: t('home.sessionFact'), value: formatInstant(expiresAt) },
          ]}
        />
      </Card>
      {/* One clear primary action on the page. When the prompt above is showing, its button IS
          the action, so this one is not repeated. */}
      {!subscriber.must_change_password ? (
        <Button type="primary" size="large" onClick={() => navigate({ kind: 'password' })}>
          {t('home.changePassword')}
        </Button>
      ) : null}
    </>
  );
}

function DashboardBody({
  contract,
  navigate,
}: {
  contract: PAContract;
  navigate: Navigation['navigate'];
}) {
  const { t } = useTranslation();
  const { query, reload } = usePAQuery<PADashboard>(() => getDashboard(contract.id), [contract.id]);
  const caps = useCapabilities();
  // FAIL CLOSED. A capability that has not arrived — or could not be read — is «not
  // permitted», because the write route enforces it and the alternative is a button whose
  // only answer is a 409.
  const selfActivation = capabilityOr(caps.query, 'self_activation_enabled', false);

  const [activating, setActivating] = useState<PAAccount | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const [activated, setActivated] = useState(false);
  // The plan choice, when the Account activates out of a tariff group (2026-08-16).
  const [options, setOptions] = useState<PAActivationOptions | null>(null);
  const [plan, setPlan] = useState<number | null>(null);

  /*
    Fetch the plan list when the confirmation dialog opens for an Account that must
    choose one. It costs the core an activation quote PER CANDIDATE, so it is asked for
    at the moment the subscriber is about to choose and at no other time — the dashboard
    itself carries only the routing flag.
  */
  useEffect(() => {
    if (!activating?.tariff_choice_required) {
      setOptions(null);
      setPlan(null);
      return;
    }
    let cancelled = false;
    void getActivationOptions(contract.id, activating.account_id)
      .then((res) => {
        if (cancelled) return;
        setOptions(res);
        // A lone plan is auto-selected: a one-item radio group is a formality, and making
        // him tap it adds a step that can only be got wrong.
        setPlan(res.options.length === 1 ? res.options[0].tariff_id : null);
      })
      .catch(() => {
        // A failed read leaves the dialog with no list; the OK button stays disabled and
        // the empty-state copy below explains it. An error banner over a transient fetch
        // would read as «активация сломана».
        if (!cancelled) setOptions(null);
      });
    return () => {
      cancelled = true;
    };
  }, [activating, contract.id]);

  const chosen: PAActivationOption | null =
    options?.options.find((o) => o.tariff_id === plan) ?? null;
  // ⚠ THE VERDICT IS THE CHOSEN PLAN'S. For an Account with no tariff the dashboard's own
  // figures are placeholders by construction (nothing to price), so gating the button on
  // them would either offer an activation that cannot work or hide one that can.
  const mayConfirm = activating?.tariff_choice_required ? (chosen?.can_activate ?? false) : true;

  const activate = async () => {
    if (!activating) return;
    if (activating.tariff_choice_required && plan == null) return;
    setBusy(true);
    setFailure(null);
    setActivated(false);
    try {
      const result = await activateAccount(contract.id, activating.account_id, plan ?? undefined);
      // The SERVER's own answer, re-read after the write: `activated` is false if the write
      // did not take, and `awaiting_activation` on the fresh dashboard is what removes the
      // button. Neither is inferred from the request having returned.
      setActivated(result.activated);
    } catch (err) {
      setFailure(err);
    } finally {
      setBusy(false);
      setActivating(null);
      // The status, the cycle and the money have all moved; only the server knows how.
      reload();
    }
  };

  return (
    <QueryFrame query={query} reload={reload}>
      {(data) => (
        <>
          {/* THE NOTICE FIRST. A suspended subscriber reads this page inside the walled garden
              with exactly one question, and the answer must not be below a fold. */}
          {data.suspension ? (
            <SuspensionNotice
              notice={data.suspension}
              currency={data.contract.currency}
              navigate={navigate}
            />
          ) : (
            <SuspendedWithoutFigure reason={data.suspended_without_figure} />
          )}

          {activated ? (
            <Alert
              type="success"
              showIcon
              message={t('dashboard.activation.done')}
              style={{ marginBottom: 20 }}
            />
          ) : null}
          {failure ? (
            <Alert
              type="error"
              showIcon
              message={errorText(failure, t)}
              action={
                <Button type="primary" onClick={() => navigate({ kind: 'topup' })}>
                  {t('dashboard.payAction')}
                </Button>
              }
              style={{ marginBottom: 20 }}
            />
          ) : null}

          <MoneyCard data={data} />
          {/* The landing page's ONE primary action from W3 onwards: the block above says
              what he owes, and this is the button that acts on it. It is here rather than
              beside the balance row because a card of facts with a button inside it reads
              as though the button belonged to the last fact. */}
          <Button type="primary" size="large" onClick={() => navigate({ kind: 'topup' })}>
            {t('dashboard.payAction')}
          </Button>
          <AccountsBlock
            accounts={data.accounts}
            currency={data.contract.currency}
            selfActivation={selfActivation}
            onActivate={setActivating}
          />

          <Modal
            open={activating !== null}
            title={t('dashboard.activation.title')}
            okText={t('dashboard.activation.confirm')}
            cancelText={t('dashboard.activation.back')}
            okButtonProps={{ loading: busy, disabled: !mayConfirm }}
            onOk={() => void activate()}
            onCancel={() => setActivating(null)}
          >
            {activating?.tariff_choice_required ? (
              <PlanChoice
                options={options}
                selected={plan}
                onSelect={setPlan}
                currency={data.contract.currency}
              />
            ) : null}
            <ul className="pa-warnings">
              <li>{t('dashboard.activation.what')}</li>
              <li>{t('dashboard.activation.network')}</li>
            </ul>
          </Modal>
        </>
      )}
    </QueryFrame>
  );
}

/**
 * THE NOTICE'S ABSENT TWIN — what the screen says when the engine can name no figure.
 *
 * ⚠ THIS BLOCK EXISTS BECAUSE THE PAGE FELL SILENT, and «silent» here meant a red status
 * column with no notice, no amount and no sentence — a page indistinguishable from a
 * working one. The server gates the notice on a NON-ZERO figure, rightly («внесите 0,00»
 * is simply false), and three ordinary paths reach zero while an account is genuinely cut
 * off: the second between the suspension and the `balance.changed` that lifts it; an
 * account with NO CYCLE ROW AT ALL, which is every imported status-1 account at cutover;
 * and a tariff the engine cannot price, which it drops from the quote with a warning.
 *
 * The two sentences are opposite and must not be merged: one says «your money is in, wait»
 * and the other says «we cannot work it out, telephone us». Telling a subscriber to wait
 * when nobody is coming is the worse of the two mistakes, which is why the server
 * distinguishes them rather than the screen guessing.
 */
function SuspendedWithoutFigure({ reason }: { reason: string }) {
  const { t } = useTranslation();
  if (reason !== 'awaiting_resume' && reason !== 'amount_unavailable') return null;
  const waiting = reason === 'awaiting_resume';
  return (
    <Alert
      className="pa-notice pa-notice-nofigure"
      type={waiting ? 'info' : 'error'}
      showIcon
      message={t(waiting ? 'dashboard.noFigure.awaitingTitle' : 'dashboard.noFigure.unavailableTitle')}
      description={t(waiting ? 'dashboard.noFigure.awaitingText' : 'dashboard.noFigure.unavailableText')}
    />
  );
}

/**
 * THE SUSPENSION NOTICE.
 *
 * The one screen in this wave whose correctness is a money question rather than a layout
 * one. Its shape is: the FIGURE, labelled with what paying it achieves; the two context rows
 * the engine sent (what is owed, what the Contract holds); the explanation when a FLOOR
 * decided the figure, because in those two cases the arithmetic a subscriber would do in his
 * head gives the wrong answer; and the list of what is cut off, carrying NO money at all.
 */
function SuspensionNotice({
  notice,
  currency,
  navigate,
}: {
  notice: PASuspension;
  currency: string;
  navigate: Navigation['navigate'];
}) {
  const { t } = useTranslation();
  const floor = floorLabel(notice.floor, t);

  return (
    <Alert
      className="pa-notice"
      type="error"
      showIcon
      message={t('dashboard.suspension.title')}
      description={
        <div className="pa-notice-body">
          <div className="pa-notice-figure">
            <span className="pa-notice-figure-label">{t('dashboard.suspension.payLabel')}</span>
            {/* VERBATIM. See this file's header: the engine's own contract-wide figure. */}
            <Money minor={notice.amount} currency={currency} strong />
          </div>

          <p className="pa-notice-note">{t('dashboard.suspension.contractWide')}</p>

          <FactList
            facts={[
              {
                key: 'requirement',
                label: t('dashboard.suspension.requirementLabel'),
                value: <Money minor={notice.requirement} currency={currency} />,
                hint: t('dashboard.suspension.requirementHint'),
              },
            ]}
          />

          {floor ? <p className="pa-notice-note">{floor}</p> : null}

          {/* THE ACTION. It navigates and carries no figure — see this file's header: the
              payment form reads `prefill_amount` from the server, which is the same
              engine answer this notice is quoting and cannot go stale between the two. */}
          <div className="pa-actions">
            <Button type="primary" size="large" onClick={() => navigate({ kind: 'topup' })}>
              {t('dashboard.suspension.payAction')}
            </Button>
            <Button onClick={() => navigate({ kind: 'instructions' })}>
              {t('dashboard.suspension.otherMethods')}
            </Button>
          </div>

          <p className="pa-notice-subtitle">{t('dashboard.suspension.accountsTitle')}</p>
          <ul className="pa-notice-accounts">
            {notice.accounts.map((a) => (
              <li key={a.account_id}>
                <span className="mono">{a.login}</span>
                {' — '}
                {suspensionReasonLabel(a.reason, t)}
                {a.suspended_at
                  ? ` (${t('dashboard.suspension.since', { when: formatInstant(a.suspended_at) })})`
                  : ''}
              </li>
            ))}
          </ul>
        </div>
      }
    />
  );
}

/**
 * The money block — §3's header rows and §2.3's property table.
 *
 * ⚠ `reserved` AND `spendable` ARE BOTH ALWAYS SHOWN, including as zero. The spec's §2.3 row
 * says «shown while a hold is active», and the wire's own comment gives the reason to go
 * further: «a missing `reserved` reads as zero, and «0 held» and «I do not know what is held»
 * are different facts». A discretionary purchase declined during the pre-funding window is
 * otherwise indistinguishable from a fault, which is a support call the row prevents.
 *
 * The promise and the installment rows appear only when they exist, because those two ARE
 * absent for most subscribers and a permanent «Обещанный платёж: 0,00» would advertise a
 * product rather than describe a state.
 */
function MoneyCard({ data }: { data: PADashboard }) {
  const { t } = useTranslation();
  const { money, contract, recommended } = data;
  const currency = contract.currency;
  // ONE label function for the badge and for the picker option, so the two cannot come to
  // say different words about the same contract.
  const contractStatus = contractStatusLabel(contract.status, t);

  const facts: Array<{ key: string; label: string; value: ReactNode; hint?: ReactNode }> = [
    {
      key: 'balance',
      label: t('dashboard.balance'),
      value: <Money minor={money.balance} currency={currency} strong colored />,
    },
    {
      key: 'credit',
      label: t('dashboard.creditLimit'),
      value: <Money minor={money.credit_limit} currency={currency} />,
      hint: t('dashboard.creditLimitHint'),
    },
  ];

  if (money.promise_remainder !== 0) {
    facts.push({
      key: 'promise',
      label: t('dashboard.promise'),
      value: <Money minor={money.promise_remainder} currency={currency} />,
      hint: money.promise_expires_at
        ? t('dashboard.promiseUntil', { when: formatInstant(money.promise_expires_at) })
        : t('dashboard.promiseHint'),
    });
  }
  if (money.installment !== 0) {
    facts.push({
      key: 'installment',
      label: t('dashboard.installmentShield'),
      value: <Money minor={money.installment} currency={currency} />,
      hint: t('dashboard.installmentShieldHint'),
    });
  }

  facts.push(
    {
      key: 'availability',
      label: t('dashboard.availability'),
      value: <Money minor={money.availability} currency={currency} colored />,
      hint: t('dashboard.availabilityHint'),
    },
    {
      key: 'reserved',
      label: t('dashboard.reserved'),
      value: <Money minor={money.reserved} currency={currency} />,
      hint: money.reserved_boundary_at
        ? t('dashboard.reservedUntil', { when: formatInstant(money.reserved_boundary_at) })
        : t('dashboard.reservedHint'),
    },
    {
      key: 'spendable',
      label: t('dashboard.spendable'),
      value: <Money minor={money.spendable} currency={currency} colored />,
      hint: t('dashboard.spendableHint'),
    },
    {
      key: 'recommended',
      label: t('dashboard.recommended'),
      value: <Money minor={recommended.amount} currency={currency} />,
      hint: recommendedModeLabel(recommended.mode, t),
    },
    {
      key: 'contract',
      label: t('dashboard.contractNumber'),
      // ⚠ THE STATUS BADGE. `status` has been on the wire since W2 and nothing rendered
      // it, while `ownedContract` admits a TERMINATED contract (it refuses only archived
      // and deleted ones) and the picker's fallback is the first row — so a former
      // customer opened his cabinet on a closed contract and read its balance and its
      // accounts with nothing saying so. D117: «harmless today and lethal the day the
      // MySQL dumps land carrying real termination dates.»
      value: (
        <>
          <span className="mono">{contract.number}</span>
          {contractStatus ? (
            <span className="pa-status pa-status-bad pa-status-badge">{contractStatus}</span>
          ) : null}
        </>
      ),
      hint: contractStatus ? t('money.contractTerminatedNote') : undefined,
    },
  );

  if (contract.payment_code) {
    facts.push({
      key: 'code',
      label: t('dashboard.paymentCode'),
      value: <span className="mono">{contract.payment_code}</span>,
      hint: t('dashboard.paymentCodeHint'),
    });
  }

  return (
    <Card className="pa-block">
      <FactList facts={facts} />
    </Card>
  );
}

/** The account cards — §3's account table, as CARDS because a row holds seven facts. */
function AccountsBlock({
  accounts,
  currency,
  selfActivation,
  onActivate,
}: {
  accounts: PAAccount[];
  currency: string;
  selfActivation: boolean;
  onActivate: (account: PAAccount) => void;
}) {
  const { t } = useTranslation();
  if (accounts.length === 0) {
    return <Alert type="info" showIcon message={t('dashboard.noAccounts')} />;
  }
  return (
    <>
      <h2 className="pa-section-title">{t('dashboard.accountsTitle')}</h2>
      {accounts.map((a) => (
        <Card
          key={a.account_id}
          className="pa-block"
          title={<span className="mono">{a.login}</span>}
          extra={<AccountStatus account={a} />}
          data-testid={`pa-account-${a.account_id}`}
        >
          <FactList facts={accountFacts(a, currency, t)} />
          {/* ⚠ F18's BUTTON, and BOTH conditions are the server's: the DERIVED
              «never activated» marker, and the operator's own switch. Only a brand-new
              Account is offered — an Account staff disabled later in its life carries
              `awaiting_activation: false` and is refused by the route with
              `accounts.activation_not_available`. */}
          {a.awaiting_activation && selfActivation ? (
            <div className="pa-actions">
              <Button type="primary" size="large" block onClick={() => onActivate(a)}>
                {t('dashboard.activate')}
              </Button>
            </div>
          ) : null}
        </Card>
      ))}
    </>
  );
}

/**
 * The status beside an account's login — the word AND its colour.
 *
 * ⚠ THE COLOUR WAS `status === 1 || status === 4 ? red : GREEN` UNTIL 2026-08-12, so an
 * account reading «Заблокирована оператором», «Приостановлена по вашей заявке»,
 * «Приостановлена по лимиту трафика» or «Отключена» was painted the same green this app
 * uses for «Активна». A colour is read before the word — that is what a colour is for —
 * so the screen told a subscriber with no service that everything was fine. The tone table
 * lives in `labels.ts` beside the label it must agree with, and an UNKNOWN code is amber
 * rather than green.
 */
function AccountStatus({ account }: { account: PAAccount }) {
  const { t } = useTranslation();
  const label = accountStatusLabel(account.status, account.awaiting_activation, t);
  const tone = accountStatusTone(account.status, account.awaiting_activation);
  return <span className={`pa-status pa-status-${tone}`}>{label}</span>;
}

function accountFacts(
  a: PAAccount,
  currency: string,
  t: TFunction,
): Array<{ key: string; label: string; value: ReactNode; hint?: ReactNode }> {
  const facts: Array<{ key: string; label: string; value: ReactNode; hint?: ReactNode }> = [
    {
      key: 'tariff',
      label: t('dashboard.tariff'),
      value: a.tariff_name || NO_VALUE,
      hint: bandwidthHint(a, t),
    },
    {
      key: 'fee',
      label: t('dashboard.subscriptionFee'),
      value: <Money minor={a.subscription_fee} currency={currency} />,
      hint: t('dashboard.subscriptionFeeHint'),
    },
    {
      // THE COST OF OWNERSHIP, said as one. See this file's header: the figure is the
      // engine's §3.3 mode 0 and it is NOT «what will be debited next» — the label is what
      // was wrong, and no «списание {{date}}» hint rides on it any more, because a date
      // beside a figure is a promise that the figure will be taken on that date.
      key: 'cost',
      label: t('dashboard.cycleCost'),
      value: <Money minor={a.cycle_cost} currency={currency} />,
      hint: cycleCostHint(a, t),
    },
    {
      // THE DATE, alone, saying only what the engine scheduled. NULL while the period is
      // suspended — which is what §2.4 asks for and what the server now delivers.
      key: 'next',
      label: t('dashboard.nextChargeDate'),
      value: a.next_charge_at ? formatInstant(a.next_charge_at) : t('dashboard.nextChargeNone'),
    },
  ];
  // The activation gate's own figures, and ONLY on the row they belong to. They are the
  // one pair on this screen that can activate him; the two money figures beside them can
  // both be strictly below the bar.
  if (a.awaiting_activation && a.activation_required !== null) {
    facts.push({
      key: 'activation',
      label: t('dashboard.activationRequired'),
      value: <Money minor={a.activation_required} currency={currency} strong />,
      hint: t('dashboard.activationRequiredHint'),
    });
  }
  if (a.awaiting_activation && a.activation_shortfall !== null) {
    facts.push({
      key: 'shortfall',
      label: t('dashboard.activationShortfall'),
      value: <Money minor={a.activation_shortfall} currency={currency} />,
      hint: t('dashboard.activationShortfallHint'),
    });
  }
  // ⚠ «Оплачено до» ONLY WHEN THE PERIOD IS PAID. Otherwise the same date is labelled as
  // what it is — the end of the current billing period — because a subscriber who has just
  // been told to pay 450,00 must not read «Оплачено до» in the next breath.
  if (a.coverage_until) {
    const paid = a.cycle_state === 'paid';
    facts.push({
      key: 'coverage',
      label: paid ? t('dashboard.coverageUntil') : t('dashboard.cycleUntil'),
      value: formatInstantDay(a.coverage_until),
      hint: paid
        ? // Model A pays by the day, so «оплачено до» means «through today» rather than
          // «through the end of the window» — the UI spec's own note on this column.
          a.billing_model === 'A'
          ? t('dashboard.coverageModelAHint')
          : undefined
        : t('dashboard.cycleUntilHint'),
    });
  }
  if (a.cycle_state) {
    facts.push({
      key: 'cycle',
      label: t('dashboard.cycleState'),
      value: cycleStateLabel(a.cycle_state, t),
    });
  }
  return facts;
}

/**
 * bandwidthHint renders the tariff's rate and the charge cadence under the tariff name.
 *
 * ⚠ THE RATE STAYS IN THE UNIT THE SERVER SENT (Кбит/с), and that is a decision. §3's own
 * example prints «12,197 Мбит/с» for what is plainly 12 197 Кбит/с — i.e. the legacy divided
 * by 1000 and relabelled — and repeating that here would render a 102 400-Кбит/с tariff as
 * «102,4 Мбит/с» instead of 100. Two units cannot both be right, the engine's is the one that
 * is checkable, and the tariff's own NAME («Домашний 100») already carries the marketing
 * figure. Converting is not forbidden by the money rule — a rate is not money — but inventing
 * a second version of a number the operator configured is the same mistake in a smaller place.
 */
function bandwidthHint(a: PAAccount, t: TFunction): string {
  const model = billingModelLabel(a.billing_model, t);
  if (a.bandwidth_kbps) {
    const speed = t('dashboard.speed', { kbps: a.bandwidth_kbps });
    return model ? `${speed} · ${model}` : speed;
  }
  return model;
}

/**
 * cycleCostHint says what the figure above it IS — and, under Model A, over what.
 *
 * ⚠ IT NO LONGER CARRIES A DATE. The hint used to read «списание {{when}}», which turned
 * two independently-true facts into one false sentence: a date on which a charge is
 * scheduled, printed under a figure that is the period's cost of ownership, reads as «this
 * amount will be taken on that date». It is not — §3.15 charges a blocked account the
 * block price or nothing, and an own-anchor service is amortised here and fired there. The
 * date now has its own row and claims only its own fact.
 *
 * ⚠ Model A: the figure is the MONTH's price, not the day's. Said on the screen because
 * the alternative was inventing a daily figure the engine does not export, which is the
 * one thing this wave forbids.
 */
function cycleCostHint(a: PAAccount, t: TFunction): string {
  const base = t('dashboard.cycleCostHint');
  return a.billing_model === 'A' ? `${base} ${t('dashboard.modelAMonthly')}` : base;
}

/**
 * THE PLAN CHOICE inside the activation dialog (customer, 2026-08-16).
 *
 * An Account created against an activation TARIFF GROUP has no plan until its subscriber
 * picks one here. Each card carries the two figures the customer named — what starting
 * this plan debits NOW (the prorated remainder of the current period) and what it costs
 * per period afterwards — plus the speed and, when he cannot afford it, the shortfall.
 *
 * # ⚠ THE VERDICT IS PER PLAN
 *
 * `can_activate` is computed per option by the core's one gate arithmetic against ONE
 * money read, so a cheap plan can be affordable on the same account where an expensive
 * one is not. The card says so on the card; the dialog's OK button follows the SELECTED
 * plan and nothing else.
 *
 * # An empty list is named, not blank
 *
 * «You must choose and there is nothing to choose from» is a real state and it is a
 * misconfiguration. Rendering it as empty space tells the subscriber the page is broken
 * and tells the operator nothing.
 */
function PlanChoice({
  options,
  selected,
  onSelect,
  currency,
}: {
  options: PAActivationOptions | null;
  selected: number | null;
  onSelect: (tariffID: number) => void;
  currency: string;
}) {
  const { t } = useTranslation();

  if (options == null) {
    return <p className="pa-hint">{t('dashboard.activation.plansLoading')}</p>;
  }
  if (options.options.length === 0) {
    return (
      <Alert
        type="warning"
        showIcon
        message={t('dashboard.activation.plansNoneTitle')}
        description={t(
          options.group_archived
            ? 'dashboard.activation.plansNoneArchived'
            : 'dashboard.activation.plansNoneBody',
        )}
        style={{ marginBottom: 16 }}
      />
    );
  }

  return (
    <div className="pa-plan-choice">
      <p className="pa-hint">
        {options.group_name
          ? t('dashboard.activation.plansLabelNamed', { group: options.group_name })
          : t('dashboard.activation.plansLabel')}
      </p>
      <div role="radiogroup" aria-label={t('dashboard.activation.plansLabel')}>
        {options.options.map((o) => (
          <div
            key={o.tariff_id}
            role="radio"
            aria-checked={selected === o.tariff_id}
            tabIndex={0}
            data-testid={`pa-plan-${o.tariff_id}`}
            className={`pa-plan-card${selected === o.tariff_id ? ' pa-plan-card-selected' : ''}`}
            onClick={() => onSelect(o.tariff_id)}
            onKeyDown={(e) => {
              // A card is a real control, so it answers the keys a control answers.
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(o.tariff_id);
              }
            }}
          >
            <div className="pa-plan-card-name">
              {o.name}
              {o.bandwidth_kbps ? (
                <span className="pa-plan-card-speed">{formatBandwidth(o.bandwidth_kbps, t)}</span>
              ) : null}
            </div>
            {o.description ? <p className="pa-plan-card-desc">{o.description}</p> : null}
            <div className="pa-plan-card-row">
              <span>{t('dashboard.activation.planChargeNow')}</span>
              {/* ⚠ «Nobody could price this plan» is NOT 0,00 ₽ — publishing a zero here
                  would advertise free activation for a plan the engine cannot charge. */}
              {o.charge_known ? (
                <Money minor={o.activation_charge} currency={currency} />
              ) : (
                <span className="pa-money">{t('dashboard.activation.planPriceUnknown')}</span>
              )}
            </div>
            <div className="pa-plan-card-row">
              <span>{t(cadenceKey(o.billing_model))}</span>
              <Money minor={o.subscription_fee} currency={currency} />
            </div>
            {o.shortfall > 0 ? (
              <div className="pa-plan-card-row">
                <span>{t('dashboard.activation.planShortfall')}</span>
                <Money minor={o.shortfall} currency={currency} colored />
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

/** How the plan charges, in the subscriber's words rather than our enum. */
function cadenceKey(billingModel: string): string {
  switch (billingModel) {
    case 'A':
      return 'dashboard.activation.planCadenceDaily';
    case 'C':
      return 'dashboard.activation.planCadencePeriod';
    default:
      // 'B' and anything unrecognised. A monthly statement is the honest default — it is
      // what every tariff on this installation does — and an unknown model must not leave
      // an empty line where a subscriber expects to read how he pays.
      return 'dashboard.activation.planCadenceMonthly';
  }
}

/**
 * «100 Мбит/с» from kbit/s.
 *
 * The UNIT comes from the catalogue and not from a literal here (ST13): every visible
 * string in this app lives in `src/i18n/ru.ts`, and `strings.test.ts` enforces it.
 */
function formatBandwidth(kbps: number, t: TFunction): string {
  if (kbps % 1024 === 0) return t('dashboard.activation.planSpeedMbit', { value: kbps / 1024 });
  // NOT rounded to a tenth of a megabit: `money-arithmetic.test.ts` bans `toFixed` in this
  // app file-wide (its subject is money, and a file-wide scan is what makes it
  // unbypassable), and inventing a precision the operator did not configure is the wrong
  // answer anyway. A rate that is not a whole number of megabits is stated in the unit it
  // was actually configured in.
  return t('dashboard.activation.planSpeedKbit', { value: kbps });
}
